import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageLoader } from '../../components/common/Loader';
import {
    IconChevronLeft, IconLoader2, IconCheck, IconX, IconAlertCircle,
    IconPlus, IconTrash, IconPencil, IconSend, IconCoins,
    IconLock, IconCheckbox,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import {
    useTravelRequest,
    useUpdateTravelRequest,
    useSubmitTravelRequest,
    useApproveTravelRequestLM,
    useApproveTravelRequestSM,
    useTravelBudget,
    useTravelBudgetByRequest,
    useCreateTravelBudget,
    useSubmitTravelBudget,
    useApproveTravelBudgetLM,
    useApproveTravelBudgetFinance,
    useTravelAdvance,
    useTravelAdvanceByRequest,
    useCreateTravelAdvance,
    useSubmitTravelAdvance,
    useApproveTravelAdvanceFinance,
    useMarkTravelAdvancePaid,
    useTravelPRV,
    useTravelPRVByRequest,
    useCreateTravelPRV,
    useSubmitTravelPRV,
    useApproveTravelPRVFinance,
    useApproveTravelPRVPartner,
    useMarkTravelPRVPaid,
} from '../../hooks/useTravel';


const CURRENCY_SYMBOLS = { NGN: '₦', USD: '$', EUR: '€', GBP: '£', CAD: 'C$', ZAR: 'R', KES: 'KSh', GHS: 'GH₵' };
const fmt = (v, curr = 'NGN') => {
    const symbol = CURRENCY_SYMBOLS[curr] || curr;
    return `${symbol}${Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_BADGE = {
    draft: 'bg-bg-light text-text-secondary border border-border',
    submitted: 'bg-sky/10 text-sky border border-sky/30',
    approved: 'bg-success/10 text-success border border-success/30',
    rejected: 'bg-danger/10 text-danger border border-danger/30',
    paid: 'bg-purple-500/10 text-purple-500 border border-purple-500/30',
};

const STATUS_LABELS = {
    draft: 'Draft',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
    paid: 'Paid',
};

function StatusBadge({ status }) {
    const cls = STATUS_BADGE[status] || 'bg-bg-light text-text-secondary border border-border';
    return (
        <span className={`px-8 py-3 rounded-full text-xs font-medium ${cls}`}>
            {STATUS_LABELS[status] || status}
        </span>
    );
}

const TRANSPORT_MODES = [
    { value: 'road', label: 'By Road' },
    { value: 'air', label: 'By Air' },
    { value: 'official', label: 'Official Vehicle' },
    { value: 'other', label: 'Other' },
];

const BUDGET_ITEM_TYPES = [
    { value: 'dsa', label: 'DSA (Daily Subsistence Allowance)' },
    { value: 'transport', label: 'Transportation' },
    { value: 'other', label: 'Other' },
];

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];

function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function Avatar({ name }) {
    const bg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    return (
        <div
            style={{ backgroundColor: bg }}
            className="w-[32px] h-[32px] rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
        >
            {getInitials(name)}
        </div>
    );
}

// ─── Approval Comment Modal

function ApprovalModal({ isOpen, onClose, title, requireComment, onConfirm, isLoading }) {
    const [comment, setComment] = useState('');

    const handleConfirm = () => {
        if (requireComment && !comment.trim()) {
            toast.error('Please provide a comment/reason.');
            return;
        }
        onConfirm(comment.trim());
    };

    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-15"
            onClick={onClose}
        >
            <div
                className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[440px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-20 border-b border-border">
                    <h5 className="text-[15px] font-semibold text-center">{title}</h5>
                </div>
                <div className="p-20 flex flex-col gap-15">
                    <div className="form-group">
                        <label className="block text-sm font-medium mb-5">
                            Comment {requireComment && <span className="text-danger">*</span>}
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={requireComment ? 'Reason is required...' : 'Optional comment...'}
                            rows={3}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                        />
                    </div>
                </div>
                <div className="flex gap-10 p-20 border-t border-border">
                    <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={isLoading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className="btn btn-primary flex-1 flex items-center justify-center gap-5"
                    >
                        {isLoading ? <IconLoader2 size={15} className="animate-spin" /> : <IconCheck size={15} />}
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Step Card Wrapper 

function StepCard({ step, title, locked, lockReason, children }) {
    return (
        <div className={`bg-bg-dark rounded-lg shadow-light border ${locked ? 'border-border/50 opacity-60' : 'border-border'} overflow-hidden`}>
            <div className="flex items-center gap-10 px-20 py-14 border-b border-border/60 bg-bg-light/30">
                <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${locked ? 'bg-border text-text-secondary' : 'bg-primary/20 text-primary'}`}>
                    {step}
                </div>
                <h3 className="font-semibold text-[14px]">{title}</h3>
                {locked && (
                    <div className="ml-auto flex items-center gap-5 text-text-secondary text-xs">
                        <IconLock size={13} />
                        {lockReason}
                    </div>
                )}
            </div>
            {!locked && (
                <div className="p-20">
                    {children}
                </div>
            )}
            {locked && (
                <div className="p-20 text-center text-text-secondary text-sm">
                    {lockReason}
                </div>
            )}
        </div>
    );
}

// ─── Step 1: Travel Request 

function Step1TravelRequest({ request, orgId, isOwner, canSecurity, canFinance, refetch }) {
    const updateRequest = useUpdateTravelRequest();
    const submitRequest = useSubmitTravelRequest();
    const approveLM = useApproveTravelRequestLM();
    const approveSM = useApproveTravelRequestSM();

    const [editing, setEditing] = useState(false);
    const [purpose, setPurpose] = useState(request.purpose || '');
    const [currency, setCurrency] = useState(request.currency || 'NGN');
    const [costCentre, setCostCentre] = useState(request.cost_centre || '');
    const [specialInstructions, setSpecialInstructions] = useState(request.special_instructions || '');
    const [legs, setLegs] = useState(
        (request.legs || []).length > 0
            ? request.legs.map(l => ({ ...l }))
            : [{ departure_date: '', return_date: '', state_city: '', estimated_nights: 0, mode_of_transport: 'road' }]
    );

    const [approvalModal, setApprovalModal] = useState({ open: false, action: null, hook: null });

    const isDraft = request.status === 'draft';
    const isRejected = request.status === 'rejected';
    const isSubmitted = request.status === 'submitted';
    const isApproved = request.status === 'approved';

    const handleSave = () => {
        const data = {
            purpose: purpose.trim(),
            currency,
            cost_centre: costCentre.trim(),
            special_instructions: specialInstructions.trim(),
            legs: legs.filter(l => l.departure_date && l.state_city).map(l => ({
                departure_date: l.departure_date,
                return_date: l.return_date || null,
                state_city: l.state_city,
                estimated_nights: l.estimated_nights ? parseInt(l.estimated_nights, 10) : 0,
                mode_of_transport: l.mode_of_transport,
            })),
        };
        updateRequest.mutate({ id: request.id, data, orgId }, {
            onSuccess: () => { toast.success('Request updated.'); setEditing(false); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Update failed.'),
        });
    };

    const handleSubmit = () => {
        submitRequest.mutate({ id: request.id, orgId }, {
            onSuccess: () => { toast.success('Request submitted for approval.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Submit failed.'),
        });
    };

    const openApproval = (action, hookFn, roleLabel, isReject) => {
        setApprovalModal({ open: true, action, hookFn, roleLabel, isReject });
    };

    const handleApprovalConfirm = (comment) => {
        const { action, hookFn, isReject } = approvalModal;
        const data = { action, comment };
        hookFn.mutate({ id: request.id, data, orgId }, {
            onSuccess: () => {
                toast.success(`Successfully ${action}d${approvalModal.roleLabel ? ` by ${approvalModal.roleLabel}` : ''}.`);
                setApprovalModal({ open: false });
                refetch();
            },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const handleLegChange = (idx, field, value) => {
        setLegs(prev => prev.map((leg, i) => {
            if (i !== idx) return leg;
            const updated = { ...leg, [field]: value };
            if (field === 'departure_date' || field === 'return_date') {
                const dep = field === 'departure_date' ? value : leg.departure_date;
                const ret = field === 'return_date' ? value : leg.return_date;
                if (dep && ret) {
                    const nights = Math.max(0, Math.round((new Date(ret) - new Date(dep)) / (1000 * 60 * 60 * 24)));
                    updated.estimated_nights = nights;
                }
            }
            return updated;
        }));
    };

    const handleAddLeg = () => setLegs(prev => [...prev, { departure_date: '', return_date: '', state_city: '', estimated_nights: 0, mode_of_transport: 'road' }]);
    const handleRemoveLeg = (idx) => setLegs(prev => prev.filter((_, i) => i !== idx));

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

    return (
        <>
            {editing ? (
                <div className="flex flex-col gap-15">
                    <div className="form-group">
                        <label className="block text-sm font-medium mb-5">Purpose of Trip <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            value={purpose}
                            onChange={e => setPurpose(e.target.value)}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                        />
                    </div>
                    <div className="form-group">
                        <label className="block text-sm font-medium mb-5">Currency <span className="text-danger">*</span></label>
                        <select
                            value={currency}
                            onChange={e => setCurrency(e.target.value)}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                        >
                            <option value="NGN">NGN — Nigerian Naira (₦)</option>
                            <option value="USD">USD — US Dollar ($)</option>
                            <option value="GBP">GBP — British Pound (£)</option>
                            <option value="EUR">EUR — Euro (€)</option>
                            <option value="CAD">CAD — Canadian Dollar (C$)</option>
                            <option value="ZAR">ZAR — South African Rand (R)</option>
                            <option value="KES">KES — Kenyan Shilling (KSh)</option>
                            <option value="GHS">GHS — Ghanaian Cedi (GH₵)</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="block text-sm font-medium mb-5">Cost Centre / Budget Line</label>
                        <input
                            type="text"
                            value={costCentre}
                            onChange={e => setCostCentre(e.target.value)}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                        />
                    </div>
                    <div className="form-group">
                        <label className="block text-sm font-medium mb-5">Special Instructions</label>
                        <textarea
                            value={specialInstructions}
                            onChange={e => setSpecialInstructions(e.target.value)}
                            rows={3}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                        />
                    </div>

                    {/* Trip Legs Editor */}
                    <div>
                        <div className="flex items-center justify-between mb-10">
                            <label className="text-sm font-medium">Trip Legs</label>
                            <button type="button" onClick={handleAddLeg} className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5">
                                <IconPlus size={13} /> Add Leg
                            </button>
                        </div>
                        {legs.map((leg, idx) => (
                            <div key={idx} className="bg-bg-light rounded-lg border border-border p-12 mb-8">
                                <div className="flex items-center justify-between mb-8">
                                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Leg {idx + 1}</span>
                                    {legs.length > 1 && (
                                        <button type="button" onClick={() => handleRemoveLeg(idx)} className="text-danger text-xs">Remove</button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-10">
                                    <div className="form-group">
                                        <label className="block text-xs font-medium mb-4">Departure Date</label>
                                        <input
                                            type="date"
                                            value={leg.departure_date}
                                            onChange={e => handleLegChange(idx, 'departure_date', e.target.value)}
                                            className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="block text-xs font-medium mb-4">Return Date</label>
                                        <input
                                            type="date"
                                            value={leg.return_date}
                                            onChange={e => handleLegChange(idx, 'return_date', e.target.value)}
                                            className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="block text-xs font-medium mb-4">State / City</label>
                                        <input
                                            type="text"
                                            value={leg.state_city}
                                            onChange={e => handleLegChange(idx, 'state_city', e.target.value)}
                                            placeholder="City / Location"
                                            className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="block text-xs font-medium mb-4">Estimated Nights</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={leg.estimated_nights}
                                            onChange={e => handleLegChange(idx, 'estimated_nights', e.target.value)}
                                            className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                        />
                                    </div>
                                    <div className="form-group col-span-2">
                                        <label className="block text-xs font-medium mb-4">Mode of Transport</label>
                                        <select
                                            value={leg.mode_of_transport}
                                            onChange={e => handleLegChange(idx, 'mode_of_transport', e.target.value)}
                                            className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                        >
                                            {TRANSPORT_MODES.map(m => (
                                                <option key={m.value} value={m.value}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-10">
                        <button onClick={() => setEditing(false)} className="btn btn-outline-secondary flex-1" disabled={updateRequest.isPending}>Cancel</button>
                        <button onClick={handleSave} disabled={updateRequest.isPending} className="btn btn-primary flex-1 flex items-center justify-center gap-5">
                            {updateRequest.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconCheck size={15} />}
                            Save Changes
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-20">
                    {/* Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-15">
                        <div>
                            <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Purpose</p>
                            <p className="text-sm font-medium">{request.purpose || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Currency</p>
                            <p className="text-sm font-medium">{request.currency || 'NGN'} — {CURRENCY_SYMBOLS[request.currency] || request.currency || '₦'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Cost Centre</p>
                            <p className="text-sm">{request.cost_centre || '-'}</p>
                        </div>
                        {request.special_instructions && (
                            <div className="md:col-span-2">
                                <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Special Instructions</p>
                                <p className="text-sm">{request.special_instructions}</p>
                            </div>
                        )}
                    </div>

                    {/* Legs Table */}
                    {request.legs && request.legs.length > 0 && (
                        <div>
                            <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-8">Trip Legs</p>
                            <div className="overflow-x-auto rounded-lg border border-border">
                                <table className="w-full text-sm">
                                    <thead className="bg-bg-light">
                                        <tr>
                                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">#</th>
                                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Date</th>
                                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Destination</th>
                                            <th className="text-center px-12 py-8 text-xs font-semibold text-text-secondary">Nights</th>
                                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Mode</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {request.legs.map((leg, idx) => (
                                            <tr key={idx} className="border-t border-border/50 hover:bg-bg-light/50 transition-colors">
                                                <td className="px-12 py-8 text-text-secondary">{idx + 1}</td>
                                                <td className="px-12 py-8">{formatDate(leg.departure_date)}</td>
                                                <td className="px-12 py-8 font-medium">{leg.state_city || leg.destination || '-'}</td>
                                                <td className="px-12 py-8 text-center">{leg.estimated_nights ?? leg.nights ?? 0}</td>
                                                <td className="px-12 py-8 capitalize">{leg.mode_of_transport || leg.mode || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Approval Status */}
                    {isSubmitted && (
                        <div className="bg-sky/5 border border-sky/20 rounded-lg p-15">
                            <p className="text-sm text-sky font-medium flex items-center gap-5">
                                <IconLoader2 size={15} className="animate-spin" />
                                Awaiting approval — request has been submitted.
                            </p>
                        </div>
                    )}

                    {isApproved && (
                        <div className="bg-success/5 border border-success/20 rounded-lg p-15">
                            <p className="text-sm text-success font-medium flex items-center gap-5">
                                <IconCheck size={15} /> Request approved. Proceed to create a travel budget below.
                            </p>
                        </div>
                    )}

                    {isRejected && request.rejection_reason && (
                        <div className="bg-danger/5 border border-danger/20 rounded-lg p-15">
                            <p className="text-xs text-danger font-semibold uppercase mb-3">Rejection Reason</p>
                            <p className="text-sm">{request.rejection_reason}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-10">
                        {(isDraft || isRejected) && isOwner && (
                            <>
                                <button onClick={() => setEditing(true)} className="btn btn-outline-secondary flex items-center gap-5">
                                    <IconPencil size={15} /> Edit
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitRequest.isPending}
                                    className="btn btn-primary flex items-center gap-5"
                                >
                                    {submitRequest.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconSend size={15} />}
                                    {isRejected ? 'Resubmit' : 'Submit for Approval'}
                                </button>
                            </>
                        )}

                        {/* Security/LM Approval Buttons */}
                        {isSubmitted && canSecurity && (
                            <>
                                <button
                                    onClick={() => openApproval('approve', approveLM, 'Line Manager', false)}
                                    className="btn btn-primary flex items-center gap-5"
                                >
                                    <IconCheck size={15} /> Approve (LM)
                                </button>
                                <button
                                    onClick={() => openApproval('reject', approveLM, 'Line Manager', true)}
                                    className="btn btn-danger flex items-center gap-5"
                                >
                                    <IconX size={15} /> Reject (LM)
                                </button>
                            </>
                        )}

                        {isSubmitted && canFinance && (
                            <>
                                <button
                                    onClick={() => openApproval('approve', approveSM, 'Security Manager', false)}
                                    className="btn btn-primary flex items-center gap-5"
                                >
                                    <IconCheck size={15} /> Approve (SM)
                                </button>
                                <button
                                    onClick={() => openApproval('reject', approveSM, 'Security Manager', true)}
                                    className="btn btn-danger flex items-center gap-5"
                                >
                                    <IconX size={15} /> Reject (SM)
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve Travel Request' : 'Reject Travel Request'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleApprovalConfirm}
                isLoading={approveLM.isPending || approveSM.isPending}
            />
        </>
    );
}

// ─── Step 2: Travel Budget

function Step2TravelBudget({ request, budget, orgId, isOwner, canFinance, refetch }) {
    const createBudget = useCreateTravelBudget();
    const submitBudget = useSubmitTravelBudget();
    const approveLM = useApproveTravelBudgetLM();
    const approveFinance = useApproveTravelBudgetFinance();

    const [showForm, setShowForm] = useState(false);
    const [approvalModal, setApprovalModal] = useState({ open: false, action: null, hookFn: null, roleLabel: null });
    const reqCurrency = request.currency || 'NGN';
    const reqSymbol = CURRENCY_SYMBOLS[reqCurrency] || reqCurrency;

    const [items, setItems] = useState([
        { description: '', item_type: 'dsa', frequency: 1, quantity: 1, rate: '' },
    ]);

    const openApproval = (action, hookFn, roleLabel) => {
        setApprovalModal({ open: true, action, hookFn, roleLabel });
    };

    const EmptyItem = () => ({ description: '', item_type: 'dsa', frequency: 1, quantity: 1, rate: '' });

    const handleItemChange = (idx, field, value) => {
        setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
    };

    const handleAddItem = () => setItems(prev => [...prev, EmptyItem()]);
    const handleRemoveItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    const calcTotal = (item) => {
        const freq = parseFloat(item.frequency) || 0;
        const qty = parseFloat(item.quantity) || 0;
        const rate = parseFloat(item.rate) || 0;
        return freq * qty * rate;
    };

    const grandTotal = items.reduce((sum, item) => sum + calcTotal(item), 0);

    const handleCreate = () => {
        const validItems = items.filter(i => i.description && i.rate);
        if (validItems.length === 0) {
            toast.error('Please add at least one budget item with a description and rate.');
            return;
        }
        const data = {
            travel_request_id: request.id,
            items: validItems.map(i => ({
                description: i.description,
                item_type: i.item_type,
                frequency: parseFloat(i.frequency) || 1,
                quantity: parseFloat(i.quantity) || 1,
                rate: parseFloat(i.rate) || 0,
                currency: reqCurrency,
            })),
        };
        createBudget.mutate({ data, orgId }, {
            onSuccess: () => { toast.success('Budget created.'); setShowForm(false); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Failed to create budget.'),
        });
    };

    const handleSubmit = () => {
        submitBudget.mutate({ id: budget.id, orgId }, {
            onSuccess: () => { toast.success('Budget submitted for approval.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Submit failed.'),
        });
    };

    const handleApprovalConfirm = (comment) => {
        const { action, hookFn } = approvalModal;
        hookFn.mutate({ id: budget.id, data: { action, comment }, orgId }, {
            onSuccess: () => { toast.success(`Successfully ${action}d${approvalModal.roleLabel ? ` by ${approvalModal.roleLabel}` : ''}.`); setApprovalModal({ open: false }); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const isDraft = !budget || budget.status === 'draft';
    const isSubmitted = budget?.status === 'submitted';
    const isApproved = budget?.status === 'approved';

    if (!budget && !showForm) {
        return (
            <div className="flex flex-col items-center gap-15 py-10">
                <p className="text-sm text-text-secondary">No budget created yet.</p>
                {isOwner && (
                    <button onClick={() => setShowForm(true)} className="btn btn-primary flex items-center gap-5">
                        <IconPlus size={15} /> Create Budget
                    </button>
                )}
            </div>
        );
    }

    if (showForm) {
        return (
            <div className="flex flex-col gap-15">
                <p className="text-sm text-text-secondary">Add budget line items for this trip.</p>

                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                        <thead className="bg-bg-light">
                            <tr>
                                <th className="text-left px-10 py-8 text-xs font-semibold text-text-secondary">Description</th>
                                <th className="text-left px-10 py-8 text-xs font-semibold text-text-secondary">Type</th>
                                <th className="text-center px-10 py-8 text-xs font-semibold text-text-secondary">Freq</th>
                                <th className="text-center px-10 py-8 text-xs font-semibold text-text-secondary">Qty</th>
                                <th className="text-right px-10 py-8 text-xs font-semibold text-text-secondary">Rate</th>
                                <th className="text-right px-10 py-8 text-xs font-semibold text-text-secondary">Total</th>
                                <th className="px-10 py-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={idx} className="border-t border-border/50">
                                    <td className="px-10 py-6">
                                        <input type="text" value={item.description}
                                            onChange={e => handleItemChange(idx, 'description', e.target.value)}
                                            placeholder="e.g. Hotel accommodation"
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none min-w-[140px]" />
                                    </td>
                                    <td className="px-10 py-6">
                                        <select value={item.item_type}
                                            onChange={e => handleItemChange(idx, 'item_type', e.target.value)}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none">
                                            {BUDGET_ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-10 py-6">
                                        <input type="number" min="1" value={item.frequency}
                                            onChange={e => handleItemChange(idx, 'frequency', e.target.value)}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none w-[60px] text-center" />
                                    </td>
                                    <td className="px-10 py-6">
                                        <input type="number" min="1" value={item.quantity}
                                            onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none w-[60px] text-center" />
                                    </td>
                                    <td className="px-10 py-6">
                                        <div className="flex items-center gap-5 justify-end">
                                            <span className="text-xs text-text-secondary font-medium">{reqSymbol}</span>
                                            <input type="number" min="0" value={item.rate}
                                                onChange={e => handleItemChange(idx, 'rate', e.target.value)}
                                                placeholder="0.00"
                                                className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none text-right min-w-[80px]" />
                                        </div>
                                    </td>
                                    <td className="px-10 py-6 text-right text-xs font-semibold text-primary">
                                        {fmt(calcTotal(item), reqCurrency)}
                                    </td>
                                    <td className="px-10 py-6">
                                        {items.length > 1 && (
                                            <button onClick={() => handleRemoveItem(idx)} className="text-danger hover:text-danger/80">
                                                <IconTrash size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-border bg-bg-light/50">
                                <td colSpan={5} className="px-10 py-8 text-sm font-bold text-right">Grand Total</td>
                                <td className="px-10 py-8 text-right font-bold text-primary">
                                    {fmt(grandTotal, reqCurrency)}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="flex gap-10 flex-wrap">
                    <button onClick={handleAddItem} className="btn btn-outline-secondary text-xs flex items-center gap-5">
                        <IconPlus size={13} /> Add Item
                    </button>
                </div>

                <div className="flex gap-10">
                    <button onClick={() => setShowForm(false)} className="btn btn-outline-secondary flex-1" disabled={createBudget.isPending}>Cancel</button>
                    <button onClick={handleCreate} disabled={createBudget.isPending} className="btn btn-primary flex-1 flex items-center justify-center gap-5">
                        {createBudget.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconCheck size={15} />}
                        Save Budget
                    </button>
                </div>
            </div>
        );
    }

    // View existing budget
    const budgetItems = budget?.items || [];
    const budgetGrandTotal = budgetItems.reduce((sum, i) => sum + parseFloat(i.total || 0), 0);

    return (
        <div className="flex flex-col gap-20">
            {/* Budget Items Table */}
            <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                    <thead className="bg-bg-light">
                        <tr>
                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Description</th>
                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Type</th>
                            <th className="text-center px-12 py-8 text-xs font-semibold text-text-secondary">Freq × Qty</th>
                            <th className="text-right px-12 py-8 text-xs font-semibold text-text-secondary">Rate</th>
                            <th className="text-right px-12 py-8 text-xs font-semibold text-text-secondary">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {budgetItems.map((item, idx) => (
                            <tr key={idx} className="border-t border-border/50 hover:bg-bg-light/40 transition-colors">
                                <td className="px-12 py-8 font-medium">{item.description}</td>
                                <td className="px-12 py-8 text-text-secondary capitalize">{item.item_type}</td>
                                <td className="px-12 py-8 text-center text-text-secondary">{item.frequency} × {item.quantity}</td>
                                <td className="px-12 py-8 text-right">{fmt(item.rate, reqCurrency)}</td>
                                <td className="px-12 py-8 text-right font-semibold text-primary">{fmt(item.total, reqCurrency)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="border-t-2 border-border bg-bg-light/50">
                            <td colSpan={4} className="px-12 py-8 text-sm font-bold text-right">Grand Total</td>
                            <td className="px-12 py-8 text-right font-bold text-primary">
                                {fmt(budgetGrandTotal, reqCurrency)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Status */}
            <div className="flex items-center gap-10">
                <span className="text-sm text-text-secondary">Status:</span>
                <StatusBadge status={budget.status} />
            </div>

            {isSubmitted && (
                <div className="bg-sky/5 border border-sky/20 rounded-lg p-15">
                    <p className="text-sm text-sky font-medium flex items-center gap-5">
                        <IconLoader2 size={15} className="animate-spin" />
                        Budget submitted — awaiting finance approval.
                    </p>
                </div>
            )}

            {isApproved && (
                <div className="bg-success/5 border border-success/20 rounded-lg p-15">
                    <p className="text-sm text-success font-medium flex items-center gap-5">
                        <IconCheck size={15} /> Budget approved. Advance request is now unlocked.
                    </p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-10">
                {isDraft && isOwner && (
                    <button
                        onClick={handleSubmit}
                        disabled={submitBudget.isPending}
                        className="btn btn-primary flex items-center gap-5"
                    >
                        {submitBudget.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconSend size={15} />}
                        Submit Budget
                    </button>
                )}

                {isSubmitted && canFinance && (
                    <>
                        <button onClick={() => openApproval('approve', approveFinance, 'Finance Director')} className="btn btn-primary flex items-center gap-5">
                            <IconCheck size={15} /> Approve (Finance)
                        </button>
                        <button onClick={() => openApproval('reject', approveFinance, 'Finance Director')} className="btn btn-danger flex items-center gap-5">
                            <IconX size={15} /> Reject (Finance)
                        </button>
                    </>
                )}
            </div>

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve Budget' : 'Reject Budget'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleApprovalConfirm}
                isLoading={approveLM.isPending || approveFinance.isPending}
            />
        </div>
    );
}

// ── Travel Advance

function Step3TravelAdvance({ request, advance, budget, orgId, isOwner, canFinance, refetch }) {
    const createAdvance = useCreateTravelAdvance();
    const submitAdvance = useSubmitTravelAdvance();
    const approveFinance = useApproveTravelAdvanceFinance();
    const markPaid = useMarkTravelAdvancePaid();

    const [requestedAmount, setRequestedAmount] = useState('');
    const [approvedAmount, setApprovedAmount] = useState('');
    const [approvalModal, setApprovalModal] = useState({ open: false, action: null, hookFn: null, roleLabel: null });

    const isDraft = !advance || advance.status === 'draft';
    const isSubmitted = advance?.status === 'submitted';
    const isApproved = advance?.status === 'approved';
    const isPaid = advance?.status === 'paid';

    useEffect(() => {
        if (advance?.auto_calculated_amount) {
            setRequestedAmount(advance.auto_calculated_amount);
        }
    }, [advance?.auto_calculated_amount]);

    const handleCreate = () => {
        const data = {
            travel_request_id: request.id,
            requested_amount: parseFloat(requestedAmount) || 0,
        };
        createAdvance.mutate({ data, orgId }, {
            onSuccess: () => { toast.success('Advance request created.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Failed.'),
        });
    };

    const handleSubmit = () => {
        submitAdvance.mutate({ id: advance.id, orgId }, {
            onSuccess: () => { toast.success('Advance submitted for approval.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Submit failed.'),
        });
    };

    const openApproval = (action, hookFn, roleLabel) => {
        setApprovalModal({ open: true, action, hookFn, roleLabel });
    };

    const handleApprovalConfirm = (comment) => {
        const { action, hookFn } = approvalModal;
        const data = { action, comment, ...(approvedAmount ? { approved_amount: parseFloat(approvedAmount) } : {}) };
        hookFn.mutate({ id: advance.id, data, orgId }, {
            onSuccess: () => {
                if (action === 'approve') {
                    // mark_paid immediately after finance approval
                    markPaid.mutate({ id: advance.id, orgId }, {
                        onSuccess: () => { toast.success('Advance approved and marked as paid.'); setApprovalModal({ open: false }); refetch(); },
                        onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Approved but failed to mark as paid.'),
                    });
                } else {
                    toast.success(`Successfully ${action}d by Finance Director.`);
                    setApprovalModal({ open: false });
                    refetch();
                }
            },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    if (!advance) {
        return (
            <div className="flex flex-col gap-15">
                <p className="text-sm text-text-secondary">
                    Budget approved. You may now request a travel advance.
                </p>
                {budget?.auto_calculated_amount && (
                    <div className="bg-bg-light rounded-lg border border-border p-15">
                        <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-5">Auto-Calculated Amount</p>
                        <p className="text-xl font-bold text-primary">{fmt(budget.auto_calculated_amount, request.currency)}</p>
                        <p className="text-xs text-text-secondary mt-3">DSA items × 75%, others × 100%</p>
                    </div>
                )}
                {isOwner && (
                    <div className="flex flex-col gap-10">
                        <div className="form-group">
                            <label className="flex items-center gap-5 text-sm font-medium mb-5"><IconCoins size={14} /> Requested Amount</label>
                            <input
                                type="number"
                                min="0"
                                value={requestedAmount}
                                onChange={e => setRequestedAmount(e.target.value)}
                                placeholder="Enter requested amount"
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                            />
                        </div>
                        <button
                            onClick={handleCreate}
                            disabled={createAdvance.isPending || !requestedAmount}
                            className="btn btn-primary flex items-center gap-5 self-start"
                        >
                            {createAdvance.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconPlus size={15} />}
                            Create Advance Request
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-20">
            {/* Advance Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-15">
                <div className="bg-bg-light rounded-lg border border-border p-15">
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Auto-Calculated</p>
                    <p className="text-lg font-bold text-text-main">{fmt(advance.auto_calculated_amount, request.currency)}</p>
                </div>
                <div className="bg-bg-light rounded-lg border border-border p-15">
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Requested</p>
                    <p className="text-lg font-bold text-primary">{fmt(advance.requested_amount, request.currency)}</p>
                </div>
                {advance.approved_amount && (
                    <div className="bg-success/10 rounded-lg border border-success/30 p-15">
                        <p className="text-xs text-success uppercase font-semibold tracking-wide mb-3">Approved</p>
                        <p className="text-lg font-bold text-success">{fmt(advance.approved_amount, request.currency)}</p>
                    </div>
                )}
            </div>

            {advance.breakdown && (
                <div>
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-8">Breakdown</p>
                    <div className="bg-bg-light rounded-lg border border-border p-15 text-sm text-text-secondary">
                        {advance.breakdown}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-10">
                <span className="text-sm text-text-secondary">Status:</span>
                <StatusBadge status={advance.status} />
            </div>

            {isSubmitted && (
                <div className="bg-sky/5 border border-sky/20 rounded-lg p-15">
                    <p className="text-sm text-sky font-medium flex items-center gap-5">
                        <IconLoader2 size={15} className="animate-spin" />
                        Advance submitted — awaiting finance approval.
                    </p>
                </div>
            )}

            {isApproved && !isPaid && (
                <div className="bg-success/5 border border-success/20 rounded-lg p-15">
                    <p className="text-sm text-success font-medium flex items-center gap-5">
                        <IconCheck size={15} /> Advance approved. Awaiting payment.
                    </p>
                </div>
            )}

            {isPaid && (
                <div className="bg-success/5 border border-success/20 rounded-lg p-15">
                    <p className="text-sm text-success font-medium flex items-center gap-5">
                        <IconCheck size={15} /> Advance paid. You may now submit a PRV (Retirement) below.
                    </p>
                </div>
            )}

            {/* Finance Approval: enter approved_amount */}
            {isSubmitted && canFinance && (
                <div className="form-group">
                    <label className="flex items-center gap-5 text-sm font-medium mb-5"><IconCoins size={14} /> Approved Amount — optional override</label>
                    <input
                        type="number"
                        min="0"
                        value={approvedAmount}
                        onChange={e => setApprovedAmount(e.target.value)}
                        placeholder={advance.requested_amount}
                        className="w-full max-w-[240px] bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                    />
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-10">
                {isDraft && isOwner && (
                    <button onClick={handleSubmit} disabled={submitAdvance.isPending} className="btn btn-primary flex items-center gap-5">
                        {submitAdvance.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconSend size={15} />}
                        Submit Advance
                    </button>
                )}

                {isSubmitted && canFinance && (
                    <>
                        <button onClick={() => openApproval('approve', approveFinance, 'Finance Director')} className="btn btn-primary flex items-center gap-5">
                            <IconCheck size={15} /> Approve & Pay
                        </button>
                        <button onClick={() => openApproval('reject', approveFinance, 'Finance Director')} className="btn btn-danger flex items-center gap-5">
                            <IconX size={15} /> Reject
                        </button>
                    </>
                )}

            </div>

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve & Pay Advance' : 'Reject Advance'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleApprovalConfirm}
                isLoading={approveFinance.isPending || markPaid.isPending}
            />
        </div>
    );
}

// ─── PRV / Retirement

function Step4PRV({ request, prv, advance, orgId, isOwner, canFinance, canPartner, refetch }) {
    const createPRV = useCreateTravelPRV();
    const submitPRV = useSubmitTravelPRV();
    const approveFinance = useApproveTravelPRVFinance();
    const approvePartner = useApproveTravelPRVPartner();
    const markPaid = useMarkTravelPRVPaid();

    const [approvalModal, setApprovalModal] = useState({ open: false, action: null, hookFn: null, roleLabel: null });
    const [showForm, setShowForm] = useState(false);

    // Bank Details
    const [accountName, setAccountName] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [sortCode, setSortCode] = useState('');
    const [address, setAddress] = useState('');
    const [notes, setNotes] = useState('');

    // Expense Items — pre-populate from advance items when available
    const [expenseItems, setExpenseItems] = useState([
        { description: '', project_code: '', budget_line: '', amount: '' },
    ]);
    const [whtPercent, setWhtPercent] = useState(0);

    useEffect(() => {
        if (!prv && advance?.items?.length > 0) {
            setExpenseItems(advance.items.map(item => ({
                description: item.description || '',
                project_code: '',
                budget_line: '',
                amount: item.full_amount || item.advance_amount || '',
            })));
        }
    }, [advance?.id, prv]);

    const isDraft = !prv || prv.status === 'draft';
    const isSubmitted = prv?.status === 'submitted';
    const isApproved = prv?.status === 'approved';
    const isPaid = prv?.status === 'paid';

    const handleExpenseChange = (idx, field, value) => {
        setExpenseItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
    };

    const handleAddExpense = () => setExpenseItems(prev => [...prev, { description: '', project_code: '', budget_line: '', amount: '' }]);
    const handleRemoveExpense = (idx) => setExpenseItems(prev => prev.filter((_, i) => i !== idx));

    const totalExpenses = expenseItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const advanceTaken = parseFloat(advance?.approved_amount || advance?.requested_amount || 0);
    const whtAmount = (totalExpenses * (parseFloat(whtPercent) || 0)) / 100;
    const netPay = totalExpenses - advanceTaken - whtAmount;

    const handleCreate = () => {
        const validItems = expenseItems.filter(i => i.description && i.amount);
        if (!accountName || !bankName || !accountNumber) {
            toast.error('Please fill in all bank details.');
            return;
        }
        if (validItems.length === 0) {
            toast.error('Please add at least one expense item.');
            return;
        }
        const data = {
            travel_request_id: request.id,
            account_name: accountName,
            bank_name: bankName,
            account_number: accountNumber,
            sort_code: sortCode,
            address: address,
            total_expenses: parseFloat(totalExpenses) || 0,
            advance_taken: parseFloat(advanceTaken) || 0,
            wht_percent: parseFloat(whtPercent) || 0,
            notes: notes,
            items: validItems.map(i => ({
                description: i.description,
                project_code: i.project_code,
                budget_line: i.budget_line,
                amount: parseFloat(i.amount) || 0,
            })),
        };
        createPRV.mutate({ data, orgId }, {
            onSuccess: () => { toast.success('PRV created.'); setShowForm(false); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Failed to create PRV.'),
        });
    };

    const handleSubmit = () => {
        submitPRV.mutate({ id: prv.id, orgId }, {
            onSuccess: () => { toast.success('PRV submitted for approval.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Submit failed.'),
        });
    };

    const openApproval = (action, hookFn, roleLabel) => {
        setApprovalModal({ open: true, action, hookFn, roleLabel });
    };

    const handleApprovalConfirm = (comment) => {
        const { action, hookFn } = approvalModal;
        hookFn.mutate({ id: prv.id, data: { action, comment }, orgId }, {
            onSuccess: () => { toast.success(`Successfully ${action}d${approvalModal.roleLabel ? ` by ${approvalModal.roleLabel}` : ''}.`); setApprovalModal({ open: false }); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const handleMarkPaid = () => {
        markPaid.mutate({ id: prv.id, orgId }, {
            onSuccess: () => { toast.success('PRV marked as paid.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Failed.'),
        });
    };

    // If PRV doesn't exist yet and owner can create
    if (!prv && !showForm) {
        return (
            <div className="flex flex-col gap-15">
                <p className="text-sm text-text-secondary">
                    Advance approved. Submit a Payment Retirement Voucher (PRV) to retire your expenses.
                </p>
                {isOwner && (
                    <button onClick={() => setShowForm(true)} className="btn btn-primary flex items-center gap-5">
                        <IconPlus size={15} /> Create PRV
                    </button>
                )}
            </div>
        );
    }

    if (showForm) {
        return (
            <div className="flex flex-col gap-20">
                {/* Bank Details */}
                <div>
                    <p className="text-sm font-semibold mb-10">Bank Details</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        <div className="form-group">
                            <label className="block text-xs font-medium mb-4">Account Name</label>
                            <input type="text" value={accountName} onChange={e => setAccountName(e.target.value)}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                        </div>
                        <div className="form-group">
                            <label className="block text-xs font-medium mb-4">Bank Name</label>
                            <input type="text" value={bankName} onChange={e => setBankName(e.target.value)}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                        </div>
                        <div className="form-group">
                            <label className="block text-xs font-medium mb-4">Account Number</label>
                            <input type="text" value={accountNumber} onChange={e => setAccountNumber(e.target.value)}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                        </div>
                        <div className="form-group">
                            <label className="block text-xs font-medium mb-4">Sort Code</label>
                            <input type="text" value={sortCode} onChange={e => setSortCode(e.target.value)}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                        </div>
                        <div className="form-group md:col-span-2">
                            <label className="block text-xs font-medium mb-4">Address</label>
                            <input type="text" value={address} onChange={e => setAddress(e.target.value)}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                        </div>
                    </div>
                </div>

                {/* Notes */}
                <div className="form-group">
                    <label className="block text-sm font-medium mb-5">Notes</label>
                    <textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={2}
                        placeholder="Additional notes or context..."
                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                    />
                </div>

                {/* Expense Items */}
                <div>
                    <div className="flex items-center justify-between mb-10">
                        <p className="text-sm font-semibold">Expense Items</p>
                        <button onClick={handleAddExpense} className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5">
                            <IconPlus size={13} /> Add Item
                        </button>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full text-sm">
                            <thead className="bg-bg-light">
                                <tr>
                                    <th className="text-left px-10 py-8 text-xs font-semibold text-text-secondary">Description</th>
                                    <th className="text-left px-10 py-8 text-xs font-semibold text-text-secondary">Project Code</th>
                                    <th className="text-left px-10 py-8 text-xs font-semibold text-text-secondary">Budget Line</th>
                                    <th className="text-right px-10 py-8 text-xs font-semibold text-text-secondary flex items-center justify-end gap-4"><IconCoins size={13} /> Amount</th>
                                    <th className="px-10 py-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenseItems.map((item, idx) => (
                                    <tr key={idx} className="border-t border-border/50">
                                        <td className="px-10 py-6">
                                            <input type="text" value={item.description}
                                                onChange={e => handleExpenseChange(idx, 'description', e.target.value)}
                                                placeholder="Expense description"
                                                className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none min-w-[140px]" />
                                        </td>
                                        <td className="px-10 py-6">
                                            <input type="text" value={item.project_code}
                                                onChange={e => handleExpenseChange(idx, 'project_code', e.target.value)}
                                                placeholder="e.g. PROJ-001"
                                                className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none" />
                                        </td>
                                        <td className="px-10 py-6">
                                            <input type="text" value={item.budget_line}
                                                onChange={e => handleExpenseChange(idx, 'budget_line', e.target.value)}
                                                placeholder="Budget line"
                                                className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none" />
                                        </td>
                                        <td className="px-10 py-6">
                                            <input type="number" min="0" value={item.amount}
                                                onChange={e => handleExpenseChange(idx, 'amount', e.target.value)}
                                                placeholder="0.00"
                                                className="w-full bg-bg-light border border-border rounded-[4px] px-8 py-[5px] text-xs focus:outline-none text-right min-w-[100px]" />
                                        </td>
                                        <td className="px-10 py-6">
                                            {expenseItems.length > 1 && (
                                                <button onClick={() => handleRemoveExpense(idx)} className="text-danger hover:text-danger/80">
                                                    <IconTrash size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* WHT */}
                <div className="form-group max-w-[200px]">
                    <label className="block text-sm font-medium mb-5">WHT % (Withholding Tax)</label>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        value={whtPercent}
                        onChange={e => setWhtPercent(e.target.value)}
                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                    />
                </div>

                {/* Summary Box */}
                <div className="bg-bg-light rounded-lg border border-border p-15">
                    <p className="text-sm font-semibold mb-10">Summary</p>
                    <div className="flex flex-col gap-8">
                        <div className="flex justify-between text-sm">
                            <span className="text-text-secondary">Total Expenses</span>
                            <span className="font-semibold">{fmt(totalExpenses, request.currency)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-text-secondary">Advance Taken</span>
                            <span className="font-semibold text-warning">- {fmt(advanceTaken, request.currency)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-text-secondary">WHT ({whtPercent}%)</span>
                            <span className="font-semibold text-warning">- {fmt(whtAmount, request.currency)}</span>
                        </div>
                        <div className="border-t border-border pt-8 flex justify-between text-sm font-bold">
                            <span>Net Pay</span>
                            <span className={netPay >= 0 ? 'text-success' : 'text-danger'}>{fmt(netPay, request.currency)}</span>
                        </div>
                    </div>
                </div>

                <div className="flex gap-10">
                    <button onClick={() => setShowForm(false)} className="btn btn-outline-secondary flex-1" disabled={createPRV.isPending}>Cancel</button>
                    <button onClick={handleCreate} disabled={createPRV.isPending} className="btn btn-primary flex-1 flex items-center justify-center gap-5">
                        {createPRV.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconCheck size={15} />}
                        Save PRV
                    </button>
                </div>
            </div>
        );
    }

    // View existing PRV
    const prvExpenseItems = prv?.items || prv?.expense_items || [];
    const prvTotal = prvExpenseItems.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    const prvAdvance = parseFloat(prv?.advance_amount || advance?.approved_amount || advance?.requested_amount || 0);
    const prvWHT = (prvTotal * parseFloat(prv?.wht_percent || 0)) / 100;
    const prvNet = prvTotal - prvAdvance - prvWHT;

    return (
        <div className="flex flex-col gap-20">
            {/* Bank Details */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-15">
                <div>
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Account Name</p>
                    <p className="text-sm font-medium">{prv?.account_name || '-'}</p>
                </div>
                <div>
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Bank</p>
                    <p className="text-sm font-medium">{prv?.bank_name || '-'}</p>
                </div>
                <div>
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Account Number</p>
                    <p className="text-sm font-medium">{prv?.account_number || '-'}</p>
                </div>
                <div>
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Sort Code</p>
                    <p className="text-sm font-medium">{prv?.sort_code || '-'}</p>
                </div>
                <div className="md:col-span-4">
                    <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Address</p>
                    <p className="text-sm font-medium">{prv?.address || '-'}</p>
                </div>
                {prv?.notes && (
                    <div className="md:col-span-4">
                        <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide mb-3">Notes</p>
                        <p className="text-sm text-text-main leading-relaxed">{prv?.notes}</p>
                    </div>
                )}
            </div>

            {/* Expense Items Table */}
            <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                    <thead className="bg-bg-light">
                        <tr>
                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Description</th>
                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Project Code</th>
                            <th className="text-left px-12 py-8 text-xs font-semibold text-text-secondary">Budget Line</th>
                            <th className="text-right px-12 py-8 text-xs font-semibold text-text-secondary">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {prvExpenseItems.map((item, idx) => (
                            <tr key={idx} className="border-t border-border/50 hover:bg-bg-light/40 transition-colors">
                                <td className="px-12 py-8 font-medium">{item.description}</td>
                                <td className="px-12 py-8 text-text-secondary">{item.project_code || '-'}</td>
                                <td className="px-12 py-8 text-text-secondary">{item.budget_line || '-'}</td>
                                <td className="px-12 py-8 text-right font-semibold">{fmt(item.amount, item.currency || request.currency)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Summary Box */}
            <div className="bg-bg-light rounded-lg border border-border p-15 max-w-[320px]">
                <p className="text-sm font-semibold mb-10">Summary</p>
                <div className="flex flex-col gap-8">
                    <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">Total Expenses</span>
                        <span className="font-semibold">{fmt(prvTotal, request.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">Advance Taken</span>
                        <span className="font-semibold text-warning">- {fmt(prvAdvance, request.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">WHT ({prv?.wht_percent || 0}%)</span>
                        <span className="font-semibold text-warning">- {fmt(prvWHT, request.currency)}</span>
                    </div>
                    <div className="border-t border-border pt-8 flex justify-between text-sm font-bold">
                        <span>Net Pay</span>
                        <span className={prvNet >= 0 ? 'text-success' : 'text-danger'}>{fmt(prvNet, request.currency)}</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-10">
                <span className="text-sm text-text-secondary">Status:</span>
                <StatusBadge status={prv.status} />
            </div>

            {isSubmitted && (
                <div className="bg-sky/5 border border-sky/20 rounded-lg p-15">
                    <p className="text-sm text-sky font-medium flex items-center gap-5">
                        <IconLoader2 size={15} className="animate-spin" />
                        PRV submitted — awaiting finance & partner approval.
                    </p>
                </div>
            )}

            {isApproved && !isPaid && (
                <div className="bg-success/5 border border-success/20 rounded-lg p-15">
                    <p className="text-sm text-success font-medium flex items-center gap-5">
                        <IconCheck size={15} /> PRV approved. Awaiting final payment.
                    </p>
                </div>
            )}

            {isPaid && (
                <div className="bg-success/5 border border-success/20 rounded-lg p-15">
                    <p className="text-sm text-success font-medium flex items-center gap-5">
                        <IconCheck size={15} /> PRV paid. Travel retirement is complete.
                    </p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-10">
                {isDraft && isOwner && (
                    <button onClick={handleSubmit} disabled={submitPRV.isPending} className="btn btn-primary flex items-center gap-5">
                        {submitPRV.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconSend size={15} />}
                        Submit PRV
                    </button>
                )}

                {isSubmitted && canFinance && (
                    <>
                        <button onClick={() => openApproval('approve', approveFinance, 'Finance Director')} className="btn btn-primary flex items-center gap-5">
                            <IconCheck size={15} /> Approve (Finance)
                        </button>
                        <button onClick={() => openApproval('reject', approveFinance, 'Finance Director')} className="btn btn-danger flex items-center gap-5">
                            <IconX size={15} /> Reject (Finance)
                        </button>
                    </>
                )}

                {isSubmitted && canPartner && (
                    <>
                        <button onClick={() => openApproval('approve', approvePartner, 'Managing Partner/COO')} className="btn btn-primary flex items-center gap-5">
                            <IconCheck size={15} /> Approve (Partner)
                        </button>
                        <button onClick={() => openApproval('reject', approvePartner, 'Managing Partner/COO')} className="btn btn-danger flex items-center gap-5">
                            <IconX size={15} /> Reject (Partner)
                        </button>
                    </>
                )}

                {isApproved && canFinance && (
                    <button onClick={handleMarkPaid} disabled={markPaid.isPending} className="btn btn-primary flex items-center gap-5">
                        {markPaid.isPending ? <IconLoader2 size={15} className="animate-spin" /> : <IconCoins size={15} />}
                        Mark PRV Paid
                    </button>
                )}
            </div>

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve PRV' : 'Reject PRV'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleApprovalConfirm}
                isLoading={approveFinance.isPending || approvePartner.isPending}
            />
        </div>
    );
}

// ─── Main Page

export default function TravelDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const canSelf = hasAnyPermission(['rbacapp.trv_self']);
    const canViewAll = hasAnyPermission(['rbacapp.trv_view_all']);
    const canSecurity = hasAnyPermission(['rbacapp.trv_security']);
    const canFinance = hasAnyPermission(['rbacapp.trv_finance']);
    const canPartner = hasAnyPermission(['rbacapp.trv_partner']);

    const { data: request, isLoading, refetch } = useTravelRequest(id, orgId);

    // Fetch linked entities by travel request ID 
    const { data: budgetByRequest, refetch: refetchBudget } = useTravelBudgetByRequest(request?.id, orgId);
    const { data: advanceByRequest, refetch: refetchAdvance } = useTravelAdvanceByRequest(request?.id, orgId);
    const { data: prvByRequest, refetch: refetchPRV } = useTravelPRVByRequest(request?.id, orgId);

    // Fall back to fetching by ID from the request object
    const budgetId = !budgetByRequest ? (request?.budget_id || request?.budget?.id) : null;
    const advanceId = !advanceByRequest ? (request?.advance_id || request?.advance?.id) : null;
    const prvId = !prvByRequest ? (request?.prv_id || request?.prv?.id) : null;

    const { data: budgetById } = useTravelBudget(budgetId || null, orgId);
    const { data: advanceById } = useTravelAdvance(advanceId || null, orgId);
    const { data: prvById } = useTravelPRV(prvId || null, orgId);

    const budget = budgetByRequest || budgetById || null;
    const advance = advanceByRequest || advanceById || null;
    const prv = prvByRequest || prvById || null;

    const isOwner = request && (
        String(request.requested_by) === String(user?.id) ||
        String(request.employee) === String(user?.employeeId) ||
        request.is_owner === true
    );

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

    const getDestinationSummary = (legs) => {
        if (!legs || legs.length === 0) return '-';
        return legs.map(l => l.destination).filter(Boolean).join(' → ') || '-';
    };

    // Step locking logic
    const requestApproved = request?.status === 'approved';
    const budgetApproved = budget?.status === 'approved';
    // PRV unlocks when advance is fully approved by both Finance and Partner (paid is also valid)
    const advancePaid = advance?.status === 'paid';

    if (isLoading) return <PageLoader />;

    if (!request) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Travel Request" path="Travel / Detail" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    Travel request not found.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="Travel Request" path="Travel / Detail" />

            {/* Back button */}
            <button
                onClick={() => navigate('/travel')}
                className="btn btn-outline-secondary flex items-center gap-5 mb-15"
            >
                <IconChevronLeft size={16} />
                Back
            </button>

            {/* Header Card */}
            <div className="bg-bg-dark rounded-lg shadow-light border border-border md:p-20 p-15 mb-20">
                <div className="flex flex-wrap items-start gap-15 justify-between">
                    <div className="flex flex-col gap-8">
                        <div className="flex items-center gap-10 flex-wrap">
                            <h2 className="text-[18px] font-bold">{request.purpose || 'Travel Request'}</h2>
                            <StatusBadge status={request.status} />
                        </div>
                        <div className="flex flex-wrap gap-20 text-sm text-text-secondary">
                            <span>
                                <span className="font-medium text-text-main">Destination:</span>{' '}
                                {getDestinationSummary(request.legs)}
                            </span>
                            <span>
                                <span className="font-medium text-text-main">Date:</span>{' '}
                                {request.legs?.[0]?.departure_date ? formatDate(request.legs[0].departure_date) : formatDate(request.created_at)}
                            </span>
                            {request.requested_by_name && (
                                <span className="flex items-center gap-5">
                                    <Avatar name={request.requested_by_name} />
                                    {request.requested_by_name}
                                </span>
                            )}
                        </div>
                        {request.cost_centre && (
                            <p className="text-xs text-text-secondary">Cost Centre: {request.cost_centre}</p>
                        )}
                    </div>
                    <div className="text-xs text-text-secondary">
                        Ref: <span className="font-mono font-semibold">TRV-{String(request.id).padStart(4, '0')}</span>
                    </div>
                </div>
            </div>

            {/* Rejection Banner */}
            {request.status === 'rejected' && (
                <div className="bg-danger/10 border border-danger/30 rounded-lg p-15 mb-20 flex items-start gap-10">
                    <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-1" />
                    <div>
                        <p className="text-sm font-semibold text-danger">Request Rejected</p>
                        {request.rejection_reason && (
                            <p className="text-sm text-text-main mt-3">{request.rejection_reason}</p>
                        )}
                    </div>
                </div>
            )}

            {/* 4-Step Stepper */}
            <div className="flex flex-col gap-15">
                {/* Step 1 */}
                <StepCard step={1} title="Travel Request" locked={false}>
                    <Step1TravelRequest
                        request={request}
                        orgId={orgId}
                        isOwner={isOwner}
                        canSecurity={canSecurity}
                        canFinance={canFinance}
                        refetch={refetch}
                    />
                </StepCard>

                {/* Step 2 */}
                <StepCard
                    step={2}
                    title="Travel Budget"
                    locked={!requestApproved}
                    lockReason="Waiting for Step 1 (Request) approval"
                >
                    {requestApproved && (
                        <Step2TravelBudget
                            request={request}
                            budget={budget}
                            orgId={orgId}
                            isOwner={isOwner}
                            canFinance={canFinance}
                            refetch={() => { refetch(); refetchBudget(); }}
                        />
                    )}
                </StepCard>

                {/* Step 3 */}
                <StepCard
                    step={3}
                    title="Travel Advance"
                    locked={!budgetApproved}
                    lockReason="Waiting for Step 2 (Budget) approval"
                >
                    {budgetApproved && (
                        <Step3TravelAdvance
                            request={request}
                            advance={advance}
                            budget={budget}
                            orgId={orgId}
                            isOwner={isOwner}
                            canFinance={canFinance}
                            refetch={() => { refetch(); refetchAdvance(); }}
                        />
                    )}
                </StepCard>

                {/* Step 4 */}
                <StepCard
                    step={4}
                    title="Payment Retirement Voucher (PRV)"
                    locked={!advancePaid}
                    lockReason="Waiting for Step 3 (Advance) to be approved"
                >
                    {advancePaid && (
                        <Step4PRV
                            request={request}
                            prv={prv}
                            advance={advance}
                            orgId={orgId}
                            isOwner={isOwner}
                            canFinance={canFinance}
                            canPartner={canPartner}
                            refetch={() => { refetch(); refetchPRV(); }}
                        />
                    )}
                </StepCard>
            </div>
        </div>
    );
}
